import React from "react";
import { Box, Button, Center, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconAlertTriangle, IconHome, IconMapOff, IconRefresh } from "@tabler/icons-react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

/**
 * Shown for an address the CRM does not have, and for an error while a page loads. Without it
 * React Router shows its own developer page ("Unexpected Application Error! ... Hey developer"),
 * which a client must never see.
 */
export default function NotFoundPage({ standalone = false, error = false }) {
  const navigate = useNavigate();

  return (
    <Center
      sx={(theme) => ({
        minHeight: standalone ? "100vh" : "60vh",
        padding: theme.spacing.md,
        backgroundColor: standalone
          ? theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0]
          : undefined,
      })}
    >
      <Box
        sx={(theme) => ({
          maxWidth: 440,
          width: "100%",
          padding: theme.spacing.xl,
          borderRadius: theme.radius.lg,
          textAlign: "center",
          backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[2]}`,
          boxShadow: theme.shadows.sm,
        })}
      >
        <Stack spacing="sm" align="center">
          <ThemeIcon size={56} radius="xl" variant="light" color={error ? "red" : undefined}>
            {error ? <IconAlertTriangle size={28} /> : <IconMapOff size={28} />}
          </ThemeIcon>
          <Title order={3}>{error ? "Something went wrong" : "Page not found"}</Title>
          <Text size="sm" color="dimmed">
            {error
              ? "This page could not be shown. Reload it, or go back to the dashboard."
              : "The address may be mistyped, or the page has moved."}
          </Text>
          <Group position="center" mt="sm" spacing="sm">
            {error && (
              <Button variant="default" leftIcon={<IconRefresh size={16} />} onClick={() => window.location.reload()}>
                Reload
              </Button>
            )}
            <Button leftIcon={<IconHome size={16} />} onClick={() => navigate("/dashboard", { replace: true })}>
              Go to dashboard
            </Button>
          </Group>
        </Stack>
      </Box>
    </Center>
  );
}

/** The router's errorElement: a missing route reads "Page not found", anything else "Something went wrong". */
export function RouteErrorPage() {
  const routeError = useRouteError();
  const notFound = isRouteErrorResponse(routeError) && routeError.status === 404;
  if (!notFound) {
    // the name only: never the message or stack of an error that may carry request data
    console.error("Route error:", routeError?.name ?? routeError?.status);
  }
  return <NotFoundPage standalone error={!notFound} />;
}
